import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { PERSONA_EXTRA_ROLLING_PRESET } from "@/features/opportunities/components/sets/CreateSetDateRangePicker/CreateSetDateRangePicker.js";
import { SetDateWindowMeta } from "@/features/opportunities/components/sets/SetDateWindowMeta/SetDateWindowMeta.js";
import { SetParamsPopover } from "@/features/opportunities/components/sets/SetParamsPopover/SetParamsPopover.js";
import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import { useRelativeTime } from "@/features/opportunities/lib/format-relative-time.js";
import { HUB_SLATE_CHIP_CLASS } from "../../page/PersonaOpportunityHubChrome/OpportunityHubSelectionPill.js";
import { OpportunityHubSetParams } from "../../page/PersonaOpportunityHubChrome/OpportunityHubSetBadge.js";
import { hubSetTypeLabel } from "../../page/PersonaOpportunityHubChrome/hub-set-type-label.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";
import { toHubSetParamsPopover } from "../../../lib/hub-set-params.js";
import { personaOpportunitySetDisplayTimestamp } from "../../../lib/persona-opportunity-set-mapper.js";
import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";

const META_LINE = "truncate text-[11px] leading-4 text-muted-foreground";
const TOOLTIP_DELAY_MS = 300;

export interface PersonaOpportunitySetCardProps {
  set: PersonaOpportunitySet;
  /** Card body opens this set. The checkbox still toggles multi-select. */
  selected?: boolean;
  onToggleSelect?: () => void;
  /** Body click and Enter/Space. Checkbox clicks do not call this. */
  onOpen?: () => void;
  /** Optional sent-to-vendor count for design/list chrome. Defaults to 0. */
  sentCount?: number;
  /** Cancel a generating set — only passed when the caller may create/generate. */
  onStop?: (set: PersonaOpportunitySet) => void;
  /** Soft-delete READY/FAILED sets — omitted while GENERATING (Stop handles cancel). */
  onDelete?: (set: PersonaOpportunitySet) => void;
  /** Re-run generation for FAILED sets. */
  onRetry?: (set: PersonaOpportunitySet) => void;
  /** Open regenerate dialog for READY sets. */
  onRegenerate?: (set: PersonaOpportunitySet) => void;
  /** Rename a READY set. */
  onRename?: (set: PersonaOpportunitySet) => void;
  /** GENERATE permission gate for mutating menu items. */
  canMutate?: boolean;
  /** Industry catalog titles. Missing entries fall back to a humanized slug. */
  industryLabelByIndex?: ReadonlyMap<string, string>;
}

function Stat({
  value,
  label,
  accentClassName,
}: {
  value: number;
  label: string;
  accentClassName?: string;
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn("text-base font-semibold tabular-nums", accentClassName ?? "text-foreground")}>
        {value}
      </span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </span>
  );
}

export function PersonaOpportunitySetCard({
  set,
  selected = false,
  onToggleSelect,
  onOpen,
  sentCount = 0,
  onStop,
  onDelete,
  onRetry,
  onRegenerate,
  onRename,
  canMutate = true,
  industryLabelByIndex,
}: PersonaOpportunitySetCardProps) {
  const { t } = useTranslation();
  const relativeDate = useRelativeTime(personaOpportunitySetDisplayTimestamp(set));
  const isGenerating = set.status === "GENERATING";
  const isFailed = set.status === "FAILED";
  const isReady = set.status === "READY";
  const isSelectMode = !!onToggleSelect;
  const isOpenMode = !!onOpen;
  const isInteractive = isSelectMode || isOpenMode;
  /** Open-mode cards host real buttons (i), menu, Stop/Retry); avoid role=button on the wrapper. */
  const cardUsesButtonRole = isSelectMode && !isOpenMode;
  const showDelete = !!onDelete && canMutate && (isReady || isFailed);
  const showRetry = !!onRetry && canMutate && isFailed;
  const showRegenerate = !!onRegenerate && canMutate && isReady;
  const showRename = !!onRename && canMutate && isReady;
  const showMenu = showDelete || showRetry || showRegenerate || showRename;

  const setParams = toHubSetParamsPopover(set, industryLabelByIndex);
  const tags = [
    set.industry ? humanizeBrandName(set.industry) : null,
    set.adPlatform ? humanizeBrandName(set.adPlatform) : null,
  ].filter((tag): tag is string => !!tag);
  const typeLabel = hubSetTypeLabel(t, set.type);

  return (
    <div
      role={cardUsesButtonRole ? "button" : undefined}
      tabIndex={cardUsesButtonRole ? 0 : undefined}
      aria-busy={isGenerating || undefined}
      aria-pressed={isSelectMode && !isOpenMode ? selected : undefined}
      aria-label={
        isOpenMode
          ? t("personaOpportunities.hub.sets.openSetAria", {
              name: set.name,
              defaultValue: `Open opportunities for ${set.name}`,
            })
          : isSelectMode
            ? t("personaOpportunities.hub.sets.selectSetAria", {
                name: set.name,
                defaultValue: `Select set ${set.name}`,
              })
            : undefined
      }
      onClick={isOpenMode ? onOpen : onToggleSelect}
      onKeyDown={(event) => {
        if (!cardUsesButtonRole) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggleSelect?.();
        }
      }}
      className={cn(
        "group/card relative flex min-h-[210px] w-full flex-col overflow-hidden rounded-md border border-border bg-card text-left transition-shadow",
        isInteractive && "cursor-pointer hover:shadow-md",
        isGenerating && "border-primary/20",
        isFailed && "border-destructive/30 bg-destructive/[0.03]",
        selected && "border-primary bg-primary/[0.03] shadow-[inset_2px_0_0_var(--primary)]",
      )}
    >
      <div
        className={cn(
          "grid flex-1",
          isSelectMode ? "grid-cols-[2.5rem_minmax(0,1fr)]" : "grid-cols-1",
        )}
      >
        {isSelectMode ? (
          <div className="flex items-start justify-center pt-3.5">
            <PersonaOpportunitySelectCheckbox
              checked={selected}
              onToggle={() => onToggleSelect?.()}
              label={t("personaOpportunities.hub.sets.selectSetAria", {
                name: set.name,
                defaultValue: `Select set ${set.name}`,
              })}
              className="mt-0.5"
            />
          </div>
        ) : null}
        <div
          className={cn(
            "flex min-w-0 flex-col pb-3 pt-3.5",
            isSelectMode ? "pr-2" : "px-4",
          )}
        >
          <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1 truncate text-sm font-semibold leading-5 text-foreground">
              {set.name}
            </div>
            <SetParamsPopover
              set={setParams.params}
              industryLabel={setParams.industryLabel}
              benchmarkIndustryLabel={setParams.benchmarkIndustryLabel}
              personas={setParams.personas}
              isGenerating={setParams.isGenerating}
            />
            {showMenu ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="size-7 shrink-0 -my-1 -translate-y-px text-muted-foreground min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
                    aria-label={t("personaOpportunities.setCard.actionsMenuAria", "Set actions")}
                  >
                    <Icon name="more-horizontal" size="sm" color="inherit" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                  {showRegenerate ? (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onRegenerate?.(set)}
                    >
                      {t("personaOpportunities.workspace.regenerate", "Regenerate")}
                    </DropdownMenuItem>
                  ) : null}
                  {showRename ? (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onRename?.(set)}
                    >
                      {t("common.rename", "Rename")}
                    </DropdownMenuItem>
                  ) : null}
                  {showRetry ? (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onRetry?.(set)}
                    >
                      {t("common.retry", "Retry")}
                    </DropdownMenuItem>
                  ) : null}
                  {(showRegenerate || showRetry || showRename) && showDelete ? <DropdownMenuSeparator /> : null}
                  {showDelete ? (
                    <DropdownMenuItem
                      className="cursor-pointer text-destructive focus:text-destructive"
                      onClick={() => onDelete?.(set)}
                    >
                      {t("common.remove", "Remove")}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          <div className="mt-1 flex min-w-0 flex-col gap-0.5">
            <span className={META_LINE}>{relativeDate}</span>
            <SetDateWindowMeta
              variant="plain"
              startDate={set.startDate}
              endDate={set.endDate}
              extraRollingPresets={[PERSONA_EXTRA_ROLLING_PRESET]}
            />
          </div>

          <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {[typeLabel, ...tags].map((tag) => (
                <Tooltip key={tag}>
                  <TooltipTrigger asChild>
                    <Badge variant="slate" className={HUB_SLATE_CHIP_CLASS}>
                      {tag}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent align="start" className="px-3 py-2">
                    <OpportunityHubSetParams set={set} />
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>

          {isFailed ? (
            <div className="mt-3 space-y-1.5">
              <p className="text-xs font-medium leading-relaxed text-destructive">
                {t("personaOpportunities.setCard.generationFailed", "Generation failed")}
              </p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t(
                  "personaOpportunities.setCard.generationFailedHint",
                  "Use Retry from the menu, create a new set, or contact support if it keeps failing.",
                )}
              </p>
              {showRetry && canMutate ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 w-fit"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRetry?.(set);
                  }}
                >
                  {t("common.retry", "Retry")}
                </Button>
              ) : null}
            </div>
          ) : isGenerating ? (
            <div className="mt-3 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="loader" size="xs" className="animate-spin text-primary" aria-hidden />
                {t("personaOpportunities.setCard.generating", "Generating…")}
              </div>
              {onStop && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={(event) => {
                    event.stopPropagation();
                    onStop(set);
                  }}
                >
                  {t("personaOpportunities.setCard.stop", "Stop")}
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {!isFailed && (
        <div
          className={cn(
            "mt-auto flex items-center gap-4 border-t border-border/60 bg-muted/40 py-2.5 pr-3",
            isSelectMode ? "pl-10" : "pl-4",
          )}
        >
          <Stat
            value={isGenerating ? 0 : set.opportunityCount}
            label={t("personaOpportunities.setCard.opportunitiesLabel", "opportunities")}
            accentClassName="text-foreground"
          />
          <Stat
            value={isGenerating ? 0 : set.scriptCount}
            label={t("personaOpportunities.setCard.scriptsLabel", "scripts")}
            accentClassName="text-primary"
          />
          <Stat
            value={isGenerating ? 0 : set.briefCount}
            label={t("personaOpportunities.setCard.briefsLabel", "briefs")}
            accentClassName="text-primary"
          />
          <Stat
            value={isGenerating ? 0 : sentCount}
            label={t("personaOpportunities.setCard.sentLabel", "sent to partner")}
            accentClassName="text-status-success"
          />
        </div>
      )}
    </div>
  );
}
